import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  addExamSetWithQuestions,
  addPYQAnalysis,
  getAIPrompt,
  getAISimplePrompt,
  getPatternDetailList,
  getSubExam,
  uploadAIFiles,
} from "../../../Action/adminPanelAction";
import { useDispatch } from "react-redux";
import PageTitle from "../../../Module/pageTitle";
import Dropdown from "../../../Component/dropdown";
import { Copy, Trash2, Wand2, Zap } from "lucide-react";
import ReactMarkdown from "react-markdown";
import {
  getExamListNew,
  getParentCourseNew,
} from "../../../Action/courseAction";
import "./AICommand.css";
import SubmitExamsetPopup from "./SubmitExamsetPopup";
import moment from "moment";
import jsPDF from "jspdf";

// ─── DOMPurify (lazy sanitizer) ───────────────────────────────────────────────
let _purify = null;
const sanitize = (html) => {
  if (typeof window === "undefined") return html;
  try {
    if (!_purify) {
      const DOMPurify = require("dompurify");
      _purify = DOMPurify;
    }
    return _purify.sanitize(html, { USE_PROFILES: { html: true } });
  } catch {
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/\bon\w+\s*=\s*["'][^"']*["']/gi, "")
      .replace(/javascript:/gi, "");
  }
};

// ─── PROMPTS ──────────────────────────────────────────────────────────────────
const COUNT_SYSTEM = `Reply with ONLY a single integer representing the total number of questions. No other text, no punctuation.`;
const COUNT_PROMPT_TEXT = `Count the total number of questions in this exam document. Reply with ONLY a single integer.`;
const DefaultPromptAnalysis = `You are an expert in exam analytics and assessment design across multiple domains.

You will receive Previous Year Question Papers (PYQs) for a given exam.

Your task is NOT to reuse or replicate questions.

Your objective is to extract a GENERALIZED EXAM INTELLIGENCE MODEL that can be used to generate NEW questions for future exams.

---

## 🎯 OBJECTIVE

Convert PYQs into a structured blueprint that defines:

* Question distribution
* Topic/subject weightage
* Difficulty levels
* Question types
* Diagram/visual weightage
* Cognitive skill requirements

This blueprint will be used by an AI system to generate new questions.

---

## 📊 PHASE 1: NORMALIZED EXTRACTION

Extract:

1. Total questions per paper

2. Subject → Unit → Topic hierarchy
   (Make it flexible, not hardcoded to any syllabus)

3. Distribution:

   * Subject-wise %
   * Unit-wise %
   * Topic-wise %

4. Question types:

   * MCQ (single correct)
   * Multi-correct
   * Assertion-Reason
   * Match the following
   * Case-based
   * Diagram-based
   * Numerical (if applicable)

5. Difficulty:

   * Easy / Moderate / Hard

6. Cognitive level:

   * Recall
   * Understanding
   * Application
   * Analysis

---

## 📊 PHASE 2: PATTERN INTELLIGENCE

Identify:

1. Recurring concept patterns (not exact questions)
2. Depth level expected per topic
3. Common distractor patterns
4. Option design logic
5. Time-pressure vs conceptual balance

---

## 📊 PHASE 3: VISUAL / DIAGRAM ANALYSIS

1. % of visual questions

2. Types:

   * Graphs
   * Biological diagrams
   * Flowcharts
   * Data tables
   * Experimental setups

3. Role of visuals:

   * Direct answer
   * Interpretation
   * Concept support

---

## ⚙️ PHASE 4: GENERATION BLUEPRINT

Return structured JSON:

{
"exam": "",
"total_questions": X,
"unit_distribution": [
{
"unit_name": "",
"weightage": %
}
],
"difficulty_distribution": {"easy": %, "moderate": %, "hard": %},
"cognitive_distribution": {},
"question_type_distribution": [],
"topic_distribution": [{topic_name:"", overall_weightage: %, unit_Wise_weightage: <only percentage>% , unit_name:""}],
"visual_distribution": {
"percentage": %,
"types": [ {
            "type": "Data tables (Match List-I and List-II)",
            "percentage": 13,
            "role": "Direct answer"
        }]
},
"constraints": {
"avg_time_per_question": "",
"negative_marking": "",
"pattern_notes": []
}
}

---

## ⚙️ PHASE 5: GENERATION RULES

Define strict rules:

1. Never reuse PYQs
2. Maintain distribution exactly
3. Generate new questions based on:

   * Same concept
   * Different context
4. Keep option quality high:

   * Close distractors
   * No guessable answers
5. Match real exam difficulty

---

## 📦 OUTPUT

1. Analysis Summary
2. Blueprint JSON
3. Generation Rules

---

IMPORTANT:
This system must work for ANY exam type and subject.
`;

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const EXTRACT_TIMEOUT_MS = 15 * 60 * 1000;
const BATCH_SIZE = 10;

// ─── MONTH NAMES ──────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// ─── JSON PARSER ──────────────────────────────────────────────────────────────
function safeParseJSON(raw) {
  if (!raw || typeof raw !== "string") return null;

  const first = raw.indexOf("{");
  const last = raw.lastIndexOf("}");
  if (first === -1 || last === -1) return null;

  let cleaned = raw.slice(first, last + 1);

  const objects = [];
  let depth = 0;
  let start = -1;

  for (let i = 0; i < cleaned.length; i++) {
    if (cleaned[i] === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (cleaned[i] === "}") {
      depth--;
      if (depth === 0 && start !== -1) {
        objects.push(cleaned.slice(start, i + 1));
        start = -1;
      }
    }
  }

  const allQuestions = [];
  for (const obj of objects) {
    try {
      const parsed = JSON.parse(obj);
      if (parsed?.questions?.length) {
        allQuestions.push(...parsed.questions);
      }
    } catch (e) {}
  }

  if (allQuestions.length) {
    return { questions: allQuestions };
  }

  return null;
}

// ─── SCALE PATTERN ────────────────────────────────────────────────────────────
function scalePattern(pattern, newTotal) {
  const origTotal = pattern.reduce((s, sec) => s + sec.totalQuestions, 0);
  const ratio = newTotal / (origTotal || newTotal);
  const isBlankPattern = origTotal === 0;
  const totalParts = pattern.reduce((s, sec) => s + sec.parts.length, 0);

  let scaledSections;

  if (isBlankPattern) {
    let remaining = newTotal;
    scaledSections = pattern.map((sec, secIdx) => {
      const isLastSection = secIdx === pattern.length - 1;
      const secShare = isLastSection
        ? remaining
        : Math.floor((sec.parts.length / totalParts) * newTotal);
      remaining -= secShare;
      return { ...sec, scaledTotal: secShare, remainder: 0 };
    });
  } else {
    scaledSections = pattern.map((sec) => ({
      ...sec,
      scaledTotal: Math.floor(sec.totalQuestions * ratio),
      remainder: (sec.totalQuestions * ratio) % 1,
    }));
    let distributed =
      newTotal - scaledSections.reduce((s, sec) => s + sec.scaledTotal, 0);
    scaledSections = scaledSections
      .map((sec, i) => ({ ...sec, i }))
      .sort((a, b) => b.remainder - a.remainder)
      .map((sec, idx) => ({
        ...sec,
        scaledTotal: sec.scaledTotal + (idx < distributed ? 1 : 0),
      }))
      .sort((a, b) => a.i - b.i);
  }

  let cursor = 1;

  return scaledSections.map((sec) => {
    const secTotal = sec.scaledTotal;
    const origSecTotal = sec.totalQuestions;
    const secRatio = secTotal / (origSecTotal || secTotal);
    const isBlankSection = origSecTotal === 0;

    let scaledParts;

    if (isBlankSection) {
      let remainingParts = secTotal;
      scaledParts = sec.parts.map((p, pIdx) => {
        const isLastPart = pIdx === sec.parts.length - 1;
        const partShare = isLastPart
          ? remainingParts
          : Math.floor(secTotal / sec.parts.length);
        remainingParts -= partShare;
        return { ...p, scaledQ: partShare, remainder: 0 };
      });
    } else {
      scaledParts = sec.parts.map((p) => ({
        ...p,
        scaledQ: Math.floor(p.totalQuestions * secRatio),
        remainder: (p.totalQuestions * secRatio) % 1,
      }));
      let partLeftover =
        secTotal - scaledParts.reduce((s, p) => s + p.scaledQ, 0);
      scaledParts = scaledParts
        .map((p, i) => ({ ...p, i }))
        .sort((a, b) => b.remainder - a.remainder)
        .map((p, idx) => ({
          ...p,
          scaledQ: p.scaledQ + (idx < partLeftover ? 1 : 0),
        }))
        .sort((a, b) => a.i - b.i);
    }

    const newParts = scaledParts.map((p) => {
      const from = cursor;
      const to =
        cursor +
        (typeof p.scaledQ === "string" ? parseInt(p.scaledQ, 10) : p.scaledQ) -
        1;
      cursor += p.scaledQ;
      return {
        questionType: p.questionType,
        questionFrom: from,
        questionTo: to,
        totalQuestions: p.scaledQ,
        correct: p.correct,
        negative: p.negative,
        _id: p._id,
        questionTypeName: p.questionTypeName,
      };
    });

    return {
      isCompulsory: sec.isCompulsory,
      name: sec.name,
      totalQuestions: secTotal,
      maxAttempt: secTotal,
      parts: newParts,
      _id: sec._id,
    };
  });
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function PyqQuestionExtraction() {
  const dispatch = useDispatch();

  // ── Multi-file queue ───────────────────────────────────────────────────────
  // Each item: { id, file, year, month, fileType, preview, status, totalQ, manualTotal, questions, logs, fileId }
  const [fileQueue, setFileQueue] = useState([]);
  const [activeFileId, setActiveFileId] = useState(null);
  const [isRunningQueue, setIsRunningQueue] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState("preview");

  const fileRef = useRef();
  const abortRef = useRef(null);
  const mathJaxLoaded = useRef(false);
  const fileIdCacheRef = useRef({}); // keyed by queue item id
  const imageMapCacheRef = useRef({}); // keyed by queue item id

  // ── Exam / template state ──────────────────────────────────────────────────
  const [aiOutput, setAiOutput] = useState(
    "// AI command output will appear here...",
  );
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [questionTypes, setQuestionTypes] = useState("");
  const [setCount, setSetCount] = useState(1);
  const [exam, setExam] = useState("");
  const [subExam, setSubExam] = useState("");
  const [examStream, setExamStream] = useState("");
  const [subExamList, setSubExamList] = useState([]);
  const [examStreamList, setExamStreamList] = useState([]);
  const [examList, setExamList] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState({
    id: 11,
    name: "PYQ Test",
    icon: "⭕",
    color: "#6366f1",
    type: 4,
  });
  const [patternList, setPatternList] = useState([]);
  const [selectedQuestionTypeList, setSelectedQuestionTypeList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [examSetName, setExamSetName] = useState("");

  // ── Derived values for system prompt ──────────────────────────────────────

  const isSectionWise = activeTemplate?.id === 1;

  // const sectionDataForPrompt = isSectionWise
  //   ? (questionTypes?.sectionData ?? [])
  //   : scaledPattern;

  let scaledPattern;

  const fetchSystemPrompt = (item) => {
    const examPatternId = item?.questionTypes?._id ?? "";
    scaledPattern = scalePattern(
      item?.questionTypes?.sectionData || [],
      totalQuestions,
    );

    const SYSTEM_PROMPT_TEXT = `
You are a JSON generator.

Return ONLY valid JSON.
No text before or after JSON.

FORMAT:
{"questions":[...]}

OUTPUT RULE:
- Return ONLY valid JSON
- NO markdown
- NO explanation
- JSON must be complete

RULES:

1. Return ONLY questions requested in range.
2. "order" must match question number.
3. Each question must follow this structure:

QUESTION TYPE:
  - questionType 1 = MCQ (Single correct answer)
  - questionType 2 = MSQ (Multiple correct answers)
  - questionType 3 = NAT (Numerical Answer Type)

{
  "complexityLevel": 1-3,
  "createdByAI": true,
  "unit": "topic",
  "order": number,
  "title": [ ...blocks ],
  "options": [
    { "descType": "text"|"img", "description": "<p>...</p>"|"<image_placeholder>", "isCorrect": true/false }
  ],
  "questionType": 1|2|3,
  "marks": number,
  "negativeMarks": number,
  "ansDescription": { "text": "<p>FULL explanation here</p>", "image": "", "video": "" },
  "decimalPlaces": number,
  "range": { "r1": 0, "r2": null },
  "isPYQ": true,
  "section": {
    "examPatternId": "${examPatternId}",
    "sectionId": "<id>",
    "partId": "<id>"
  }
}

─── MULTI-BLOCK RULE ───────────────────────────────────────────────────────────

"title" and each option's content may contain MULTIPLE blocks in the order they appear.
Each block is either:
  { "descType": "text",  "description": "<p>text here</p>" }
  { "descType": "img", "description": "<image_placeholder>" }

─── SECTION DATA ───────────────────────────────────────────────────────────────

${JSON.stringify(scaledPattern)}

Each section has "_id" (sectionId) and "parts" array. Each part has:
- "_id" (partId)
- "questionType" (1 = MCQ, 2 = MSQ, 3 = NAT)
- "correct" (marks for correct answer)
- "negative" (negative marks for wrong answer)

─── IMPORTANT ──────────────────────────────────────────────────────────────────

- "section.sectionId" → "_id" of the matching section
- "section.partId"    → "_id" of the part whose "questionType" matches this question
- "marks"             → value of "correct" from the matching part
- "negativeMarks"     → value of "negative" from the matching part
- MCQ → 4 options, only 1 isCorrect: true
- MSQ → 4 options, multiple isCorrect: true
- NAT → options = []
- Wrap all text in <p></p>
- Use MathJax \\(...\\)
- Escape backslashes properly (\\\\)
- decimalPlaces: NAT only (0, 1, 2, or 3); always 0 for MCQ/MSQ

Rules:
- Preserve the exact order of text and image as they appear in the source.
- If a question title is: [text] → [image] → [text], produce 3 blocks in that order.
- If a question title is text only, produce a single text block.
- If a question title is image only, produce a single image block.
- Apply the same multi-block logic to each option and to ansDescription.text.
- "ansDescription.text" MUST contain the COMPLETE answer explanation. Include ALL of the following:
    • Axiom / principle
    • Step-by-step deduction
    • Why each wrong option is wrong
    • Exam connection
    • Speed hack
    • Remember note

`;

    return SYSTEM_PROMPT_TEXT;
  };

  // ── Exam list init ─────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(
      getExamListNew((res) => {
        setExamList(res);
      }),
    );
  }, []);

  const onChangeExam = (value) => {
    setExam(value);
    setSubExam("");
    setSubExamList([]);
    setExamStream("");
    if (value) {
      dispatch(
        getSubExam({ examTypeId: value._id }, (res) => {
          if (res.data.length > 0) {
            setSubExamList(res.data);
          } else {
            dispatch(
              getParentCourseNew(`${value._id}`, (res) => {
                setExamStreamList(res);
              }),
            );
          }
        }),
      );
    }
  };

  const onChangeSubExam = (value) => {
    setSubExam(value);
    setExamStream("");
    if (value) {
      dispatch(
        getParentCourseNew(`${exam._id}/${value._id}`, (res) => {
          setExamStreamList(res);
        }),
      );
    }
  };

  const onChangeExamStream = (value) => {
    setExamStream(value);
    if (value) {
      const postData = {
        isModelSet: true,
        examTypeId: exam?._id,
        subExamTypeId: subExam?._id,
        baseCourseId: value?._id,
      };
      dispatch(
        getPatternDetailList(postData, (res) => {
          const newArray = [{ _id: 0, name: "Pattern" }, ...res];
          setPatternList(newArray);
        }),
      );
    }
  };

  const getQuestionTypesFromSections = (sections = []) => {
    const types = new Set();
    sections.forEach((section) => {
      section.parts?.forEach((part) => {
        if (part?.questionTypeName) types.add(part.questionTypeName);
      });
    });
    return Array.from(types);
  };

  const onChangeQuestionPattern = (val) => {
    setQuestionTypes(val);
    setSelectedQuestionTypeList(getQuestionTypesFromSections(val?.sectionData));
  };

  // ── API call wrapper ───────────────────────────────────────────────────────
  const callAPIPromise = (
    systemPromptArr,
    promptArr,
    signal,
    mode = "extract",
  ) =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      const onAbort = () => reject(new DOMException("Aborted", "AbortError"));
      signal?.addEventListener("abort", onAbort, { once: true });

      dispatch(
        getAIPrompt(
          { systemPrompt: systemPromptArr, prompt: promptArr, mode },
          (res) => {
            signal?.removeEventListener("abort", onAbort);
            if (signal?.aborted) {
              reject(new DOMException("Aborted", "AbortError"));
              return;
            }
            if (!res) {
              reject(new Error("No response from API"));
              return;
            }

            const content = res?.content ?? res?.data?.content ?? [];
            const textBlock = Array.isArray(content)
              ? content.find((b) => b.type === "text")
              : null;
            const text =
              textBlock?.text ??
              (typeof res?.output === "string" ? res.output : "") ??
              (typeof res === "string" ? res : "");
            const turns = textBlock?.turns ?? res?.turns ?? null;
            const usage = res?.usage ?? res?.data?.usage ?? null;

            if (!text) {
              reject(new Error("API returned empty text"));
              return;
            }
            resolve({ text, turns, usage });
          },
        ),
      );
    });

  const callAnalysisAPIPromise = (systemPromptArr, promptArr, signal) =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      const onAbort = () => reject(new DOMException("Aborted", "AbortError"));
      signal?.addEventListener("abort", onAbort, { once: true });

      dispatch(
        getAISimplePrompt(
          { systemPrompt: systemPromptArr, prompt: promptArr },
          (res) => {
            signal?.removeEventListener("abort", onAbort);
            if (signal?.aborted) {
              reject(new DOMException("Aborted", "AbortError"));
              return;
            }
            if (!res) {
              reject(new Error("No response from API"));
              return;
            }

            const content = res?.content ?? res?.data?.content ?? res ?? [];
            // const textBlock = Array.isArray(content)
            //   ? content.find((b) => b.type === "text")
            //   : null;
            // const text =
            //   textBlock?.text ??
            //   (typeof res?.output === "string" ? res.output : "") ??
            //   (typeof res === "string" ? res : "");
            // const turns = textBlock?.turns ?? res?.turns ?? null;
            // const usage = res?.usage ?? res?.data?.usage ?? null;

            // if (!text) {
            //   reject(new Error("API returned empty text"));
            //   return;
            // }
            resolve({ content });
          },
        ),
      );
    });

  const extractJSON = (text) => {
    if (!text) return null;

    let start = text.indexOf("{");
    let end = text.lastIndexOf("}");

    while (start !== -1 && end !== -1 && start < end) {
      const possibleJSON = text.slice(start, end + 1);

      try {
        return JSON.parse(possibleJSON);
      } catch (e) {
        // move start forward to next {
        start = text.indexOf("{", start + 1);
      }
    }

    return null;
  };

  // ── Upload promise wrappers ────────────────────────────────────────────────
  const uploadFilePromise = (f) =>
    new Promise((resolve, reject) => {
      dispatch(
        uploadAIFiles(f, (res) => {
          if (res?.fileId) resolve(res);
          else reject(new Error("Upload failed — no fileId returned"));
        }),
      );
    });

  const extractPdfImagesPromise = (f) =>
    new Promise((resolve) => {
      const formData = new FormData();
      formData.append("file", f);
      const url =
        process.env.REACT_APP_BASEURL + "/v1/claudeAI/extract-pdf-images";
      fetch(url, { method: "POST", body: formData })
        .then((r) => r.json())
        .then((res) => resolve(res?.data || { imageMap: {}, totalImages: 0 }))
        .catch(() => resolve({ imageMap: {}, totalImages: 0 }));
    });

  // ── Replace image placeholders ─────────────────────────────────────────────
  const replacePlaceholders = (questionsList, imageMap) => {
    if (!Object.keys(imageMap).length) return questionsList;

    const replaceInHtml = (html) => {
      if (!html || typeof html !== "string") return html;
      return html.replace(
        /<image_placeholder[^>]*data-key="([^"]+)"[^>]*\/?>/g,
        (_, key) => {
          const url = imageMap[key];
          return url
            ? `${url}`
            : `<span style="color:#f87171;font-size:11px">[Image: ${key} not found]</span>`;
        },
      );
    };

    const replaceInBlocks = (blocks) =>
      (blocks || []).map((block) => {
        if (block.descType !== "img") return block;
        const keyMatch = block.description?.match(/data-key="([^"]+)"/);
        const key = keyMatch?.[1];
        const url = key ? imageMap[key] : null;
        return {
          ...block,
          description: url
            ? `${url}`
            : `<span style="color:#f87171">[Image not found${key ? `: ${key}` : ""}]</span>`,
        };
      });

    return questionsList.map((q) => ({
      ...q,
      title: replaceInBlocks(q.title),
      options: (q.options || []).map((opt) => ({
        ...opt,
        description:
          opt.descType === "img"
            ? replaceInBlocks([opt])[0]?.description
            : replaceInHtml(opt.description),
      })),
      ansDescription: {
        ...q.ansDescription,
        text: replaceInHtml(q.ansDescription?.text),
      },
    }));
  };

  // ── Build prompt helpers ───────────────────────────────────────────────────
  const buildSystemPrompt = (text) => [
    { type: "text", text, cache_control: { type: "ephemeral" } },
  ];

  const buildPrompt = (fId, instructionText) => [
    { type: "document", source: { type: "file", file_id: fId } },
    { type: "text", text: instructionText },
  ];

  // ── Queue helpers ──────────────────────────────────────────────────────────
  const updateQueueItem = (id, patch) => {
    setFileQueue((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const addItemLog = (id, msg, type = "info") => {
    setFileQueue((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              logs: [
                ...item.logs,
                { msg, type, time: new Date().toLocaleTimeString() },
              ],
            }
          : item,
      ),
    );
  };

  // ── Add files to queue ─────────────────────────────────────────────────────
  const handleFileDrop = (files) => {
    const now = new Date();
    const newItems = Array.from(files)
      .filter(
        (f) => f.type.startsWith("image/") || f.type === "application/pdf",
      )
      .map((f) => ({
        id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        file: f,
        year: now.getFullYear().toString(),
        month: String(now.getMonth() + 1).padStart(2, "0"),
        fileType: f.type === "application/pdf" ? "pdf" : "image",
        preview: URL.createObjectURL(f),
        status: "pending",
        totalQ: "",
        manualTotal: "",
        questions: [],
        logs: [],
        fileId: null,
        questionTypes: {},
        pyqAnalysis: "",
      }));
    setFileQueue((prev) => [...prev, ...newItems]);
    if (newItems.length > 0 && !activeFileId) {
      setActiveFileId(newItems[0].id);
    }
  };

  const removeFromQueue = (id) => {
    setFileQueue((prev) => prev.filter((item) => item.id !== id));
    if (activeFileId === id) {
      setActiveFileId(null);
    }
    delete fileIdCacheRef.current[id];
    delete imageMapCacheRef.current[id];
  };

  // ── Upload per item ────────────────────────────────────────────────────────
  const ensureFileUploadedForItem = async (item) => {
    if (fileIdCacheRef.current[item.id]) return fileIdCacheRef.current[item.id];

    addItemLog(item.id, "📤 Uploading file & extracting images...", "info");
    updateQueueItem(item.id, { status: "uploading" });

    const [anthropicRes, imageRes] = await Promise.all([
      uploadFilePromise(item.file),
      item.fileType === "pdf"
        ? extractPdfImagesPromise(item.file)
        : Promise.resolve({ imageMap: {}, totalImages: 0 }),
    ]);

    fileIdCacheRef.current[item.id] = anthropicRes.fileId;
    imageMapCacheRef.current[item.id] = imageRes.imageMap || {};

    const imgCount = imageRes.totalImages || 0;
    addItemLog(
      item.id,
      `✅ Uploaded. ${imgCount > 0 ? `Found ${imgCount} images.` : "No images found."}`,
      "success",
    );

    return anthropicRes.fileId;
  };

  // ── Auto-detect count for a single item ───────────────────────────────────
  const countQuestionsForItem = async (item) => {
    updateQueueItem(item.id, { status: "counting" });
    addItemLog(item.id, "🔍 Counting questions...", "info");
    try {
      const fId = await ensureFileUploadedForItem(item);
      const system = buildSystemPrompt(COUNT_SYSTEM);
      const prompt = buildPrompt(fId, COUNT_PROMPT_TEXT);
      const { text } = await callAPIPromise(system, prompt, null, "count");
      const n = parseInt(text.replace(/\D/g, "").trim(), 10);
      if (!isNaN(n) && n > 0) {
        updateQueueItem(item.id, {
          totalQ: n,
          manualTotal: String(n),
          status: "pending",
        });
        addItemLog(item.id, `✅ Detected ${n} questions`, "success");
      } else {
        addItemLog(
          item.id,
          `⚠️ Could not parse count (got: "${text}"). Enter manually.`,
          "warn",
        );
        updateQueueItem(item.id, { totalQ: "?", status: "pending" });
      }
    } catch (e) {
      addItemLog(item.id, `❌ Count failed: ${e.message}`, "error");
      updateQueueItem(item.id, { totalQ: "?", status: "pending" });
    }
  };

  // ── Extract a single queue item ────────────────────────────────────────────
  const extractItem = async (item, controller) => {
    const total = parseInt(item.manualTotal);
    if (!total || total < 1) {
      addItemLog(item.id, "⚠️ Set total questions first", "warn");
      updateQueueItem(item.id, { status: "failed" });
      return;
    }

    updateQueueItem(item.id, { status: "uploading", questions: [], logs: [] });

    let fId;
    try {
      fId = await ensureFileUploadedForItem(item);
    } catch (e) {
      addItemLog(item.id, `❌ Upload failed: ${e.message}`, "error");
      updateQueueItem(item.id, { status: "failed" });
      return;
    }

    updateQueueItem(item.id, { status: "extracting" });

    const imageMap = imageMapCacheRef.current[item.id] || {};
    const imageContext =
      Object.keys(imageMap).length > 0
        ? `\n─── AVAILABLE IMAGES ─────────────────────────────────────────────────────────\n
        The PDF contains the following extracted images. When a question or option references
        an image/figure/diagram, use the closest matching key below as the placeholder:\n
        ${Object.keys(imageMap)
          .map((k) => `  ${k}`)
          .join("\n")}

        Use this format in your JSON output wherever an image should appear:
          { "descType": "img", "description": "<image_placeholder data-key=\\"KEY\\">" }

        Replace KEY with the matching key from the list above.
        Match by page number — if the question is on page 3, prefer page_3_img_* keys.
        ─────────────────────────────────────────────────────────────────────────────────`
        : "";
    const system = buildSystemPrompt(fetchSystemPrompt(item) + imageContext);

    const systemAnalysisPrompt = buildSystemPrompt(DefaultPromptAnalysis);
    const promptAnalysis = [
      {
        type: "document",
        source: { type: "file", file_id: fId },
      },
    ];

    const batches = [];
    for (let start = 1; start <= total; start += BATCH_SIZE) {
      batches.push({
        from: start,
        to: Math.min(start + BATCH_SIZE - 1, total),
      });
    }

    addItemLog(
      item.id,
      `🚀 Extracting ${total} questions in ${batches.length} batches of ${BATCH_SIZE}`,
      "info",
    );

    const correctSection = (q) => {
      for (const section of scaledPattern) {
        for (const part of section.parts || []) {
          if (q.order >= part.questionFrom && q.order <= part.questionTo) {
            if (
              q.section?.sectionId !== section._id ||
              q.section?.partId !== part._id
            ) {
              addItemLog(
                item.id,
                `🔧 Q${q.order}: fixed sectionId ` +
                  `"${q.section?.sectionId || "EMPTY"}" → "${section._id}"`,
                "warn",
              );
            }
            q.section = {
              examPatternId: q.section.examPatternId,
              sectionId: section._id,
              partId: part._id,
            };
            q.marks = part.correct;
            q.negativeMarks = part.negative;
            return q;
          }
        }
      }
      addItemLog(
        item.id,
        `⚠️ Q${q.order}: no matching range — section not assigned`,
        "warn",
      );
      return q;
    };

    for (let b = 0; b < batches.length; b++) {
      if (controller.signal.aborted) break;

      const { from, to } = batches[b];
      addItemLog(
        item.id,
        `📦 Batch ${b + 1}/${batches.length}: Q${from}–${to}`,
        "info",
      );

      const userPrompt = `Extract questions from ${from} to ${to} (inclusive).

      CRITICAL:
      - Output MUST be STRICT JSON
      - FIRST character MUST be "{"
      - LAST character MUST be "}"
      - NO text before JSON
      - NO text after JSON

      FORMAT:
      {"questions":[...]}

      If fail:
      {"questions":[]}`;

      const prompt = buildPrompt(fId, userPrompt);

      const tryOnce = () =>
        new Promise((resolve, reject) => {
          if (controller.signal.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
          }
          const timer = setTimeout(
            () => reject(new Error(`Timeout after ${EXTRACT_TIMEOUT_MS}ms`)),
            EXTRACT_TIMEOUT_MS,
          );
          const onGlobalAbort = () => {
            clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
          };
          controller.signal.addEventListener("abort", onGlobalAbort, {
            once: true,
          });

          callAPIPromise(system, prompt, null, "extract")
            .then((result) => {
              clearTimeout(timer);
              controller.signal.removeEventListener("abort", onGlobalAbort);
              resolve(result);
            })
            .catch((err) => {
              clearTimeout(timer);
              controller.signal.removeEventListener("abort", onGlobalAbort);
              reject(err);
            });
        });

      let success = false;

      for (let attempt = 1; attempt <= 3; attempt++) {
        if (controller.signal.aborted) break;
        try {
          const { text } = await tryOnce();
          const first = text.indexOf("{");
          const last = text.lastIndexOf("}");
          if (first === -1 || last === -1)
            throw new Error("No JSON in response");

          const parsed = safeParseJSON(text.slice(first, last + 1));
          if (!parsed?.questions?.length)
            throw new Error("Empty questions array");

          // ✅ ADD HERE — auto-correct section assignment by order range
          parsed.questions = parsed.questions.map(correctSection);

          const valid = parsed.questions.filter(
            (q) => q.order >= from && q.order <= to,
          );
          const accepted =
            valid.length > 0
              ? valid
              : parsed.questions
                  .slice(0, to - from + 1)
                  .map((q, i) => ({ ...q, order: from + i }));

          setFileQueue((prev) =>
            prev.map((qi) => {
              if (qi.id !== item.id) return qi;
              const map = new Map(qi.questions.map((q) => [q.order, q]));
              accepted.forEach((q) => map.set(q.order, q));
              return {
                ...qi,
                questions: Array.from(map.values()).sort(
                  (a, b) => a.order - b.order,
                ),
              };
            }),
          );

          addItemLog(
            item.id,
            `✅ Batch ${b + 1}: ${accepted.length} questions`,
            "success",
          );
          success = true;
          break;
        } catch (e) {
          if (controller.signal.aborted) break;
          addItemLog(
            item.id,
            `⚠️ Batch ${b + 1} attempt ${attempt} failed: ${e.message}`,
            "warn",
          );
          if (attempt < 3)
            await new Promise((r) => setTimeout(r, 1000 * attempt));
        }
      }

      if (!success && !controller.signal.aborted) {
        addItemLog(
          item.id,
          `❌ Batch ${b + 1} (Q${from}–${to}): permanently failed`,
          "error",
        );
      }

      if (b < batches.length - 1 && !controller.signal.aborted) {
        await new Promise((r) => setTimeout(r, 400));
      }
    }

    callAnalysisAPIPromise(systemAnalysisPrompt, promptAnalysis, null, null)
      .then((result) => {
        // clearTimeout(timer);
        // controller.signal.removeEventListener("abort", onGlobalAbort);
        updateQueueItem(item.id, {
          pyqAnalysis: result?.content,
        });
        const extractJSONData = extractJSON(result?.content);
        extractJSONData["examTypeId"] = exam?._id;
        extractJSONData["baseCourseId"] = examStream?._id;
        if (subExam) {
          extractJSONData["subExamTypeId"] = subExam?._id;
        }
        dispatch(addPYQAnalysis(extractJSONData, (res) => {}));
        // resolve(result);
      })
      .catch((err) => {
        // clearTimeout(timer);
        // controller.signal.removeEventListener("abort", onGlobalAbort);
        // reject(err);
      });

    // Final: replace placeholders and update status
    setFileQueue((prev) =>
      prev.map((qi) => {
        if (qi.id !== item.id) return qi;
        const withImages = replacePlaceholders(qi.questions, imageMap);
        return {
          ...qi,
          questions: withImages,
          status: controller.signal.aborted ? "failed" : "done",
        };
      }),
    );

    // Use current state for final log
    setFileQueue((prev) => {
      const qi = prev.find((i) => i.id === item.id);
      if (qi) {
        const finalCount = qi.questions.length;
        addItemLog(
          item.id,
          `🏁 Done. ${finalCount}/${total} questions extracted.`,
          "success",
        );
      }
      return prev;
    });

    return new Promise((resolve) => {
      setFileQueue((prev) => {
        const qi = prev.find((i) => i.id === item.id);
        if (qi) {
          const finalCount = qi.questions.length;
          addItemLog(
            item.id,
            `🏁 Done. ${finalCount}/${total} questions extracted.`,
            "success",
          );
          // Resolve with the final item data
          resolve(controller.signal.aborted ? null : qi);
        } else {
          resolve(null);
        }
        return prev;
      });
    });
  };

  // ── Run entire queue sequentially ──────────────────────────────────────────
  const runQueue = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setIsRunningQueue(true);

    const snapshot = fileQueue.filter(
      (item) => item.status === "pending" || item.status === "failed",
    );

    for (const item of snapshot) {
      if (controller.signal.aborted) break;
      setActiveFileId(item.id);
      setActiveTab("preview");

      // Extract and get completed item back
      const completedItem = await extractItem(item, controller);

      // Save this item before moving to next
      if (completedItem && !controller.signal.aborted) {
        try {
          await saveExamset(completedItem);
          addItemLog(item.id, "💾 Saved successfully", "success");
        } catch (e) {
          addItemLog(item.id, `❌ Save failed: ${e.message}`, "error");
        }
      }

      setActiveTab("questions");
    }

    setIsRunningQueue(false);
    loadMathJax();
    typesetMath();
  };

  const cancelQueue = () => {
    abortRef.current?.abort();
    setIsRunningQueue(false);
  };

  // ── Extract only a specific item (manual trigger) ──────────────────────────
  const extractSingleItem = async (item) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setIsRunningQueue(true);
    setActiveFileId(item.id);
    await extractItem(item, controller);
    setIsRunningQueue(false);
    loadMathJax();
    typesetMath();
  };

  // ── MathJax ───────────────────────────────────────────────────────────────
  const loadMathJax = () => {
    if (mathJaxLoaded.current || document.getElementById("mathjax-script"))
      return;
    mathJaxLoaded.current = true;
    window.MathJax = {
      tex: {
        inlineMath: [
          ["\\(", "\\)"],
          ["$", "$"],
        ],
        displayMath: [
          ["\\[", "\\]"],
          ["$$", "$$"],
        ],
      },
      startup: { typeset: false },
    };
    const sc = document.createElement("script");
    sc.id = "mathjax-script";
    sc.src =
      "https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-chtml.min.js";
    sc.async = true;
    sc.onload = () => window.MathJax?.typesetPromise?.();
    document.head.appendChild(sc);
  };

  const typesetMath = () => {
    setTimeout(() => {
      if (window.MathJax?.typesetPromise) window.MathJax.typesetPromise();
    }, 300);
  };

  const copyJSON = (questions) =>
    navigator.clipboard.writeText(
      JSON.stringify({ totalExtracted: questions.length, questions }, null, 2),
    );

  const stripHTML = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const loadImage = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = url;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = () => resolve(null);
    });
  };

  const getFullContent = (docItem) => {
    let questionText = "";
    let questionImages = [];

    docItem.title?.forEach((item) => {
      if (item.descType === "text") {
        questionText += stripHTML(item.description) + " ";
      } else if (item.descType === "img") {
        questionImages.push(item.description);
      }
    });

    let optionsText = "";
    let optionImages = [];

    docItem.options?.forEach((opt, i) => {
      const label = String.fromCharCode(65 + i);

      if (opt.descType === "text") {
        optionsText += `${label}. ${stripHTML(opt.description)}\n`;
      } else if (opt.descType === "img") {
        optionsText += `${label}. [Image]\n`; // 👈 placeholder
        optionImages.push(opt.description);
      }
    });

    let answerImages = [];
    if (docItem.ansDescription?.image) {
      answerImages.push(docItem.ansDescription.image);
    }

    return {
      questionText,
      optionsText,
      questionImages,
      optionImages,
      answerImages,
    };
  };

  const handleExport = async (questionData) => {
    const doc = new jsPDF();
    let startY = 10;

    const pageWidth = doc.internal.pageSize.getWidth();

    const pageHeight = doc.internal.pageSize.getHeight();

    const checkPageBreak = (requiredHeight = 20) => {
      if (startY + requiredHeight > pageHeight - 10) {
        doc.addPage();
        startY = 10;
      }
    };

    for (let partIndex = 0; partIndex < questionData.length; partIndex++) {
      const part = questionData[partIndex];
      const partName = part.fileName || "";
      const documents = part.questions || [];

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`${partName} (Total: ${documents.length})`, 10, startY);

      startY += 6;

      const imageCache = {};

      // ✅ preload ALL images
      for (let docItem of documents) {
        const { questionImages, optionImages, answerImages } =
          getFullContent(docItem);

        const allImages = [...questionImages, ...optionImages, ...answerImages];

        for (let url of allImages) {
          if (url && !imageCache[url]) {
            imageCache[url] = await loadImage(url);
          }
        }
      }

      // =========================
      // Questions Loop
      // =========================

      for (let index = 0; index < documents.length; index++) {
        const q = documents[index];

        checkPageBreak(40);

        // =========================
        // Question Header
        // =========================

        doc.setFillColor(240, 240, 240);

        doc.rect(8, startY - 5, 194, 10, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);

        doc.text(`Q${index + 1}`, 10, startY + 1);

        doc.setFontSize(9);

        doc.text(
          `Marks: +${q.marks || 0} / -${q.negativeMarks || 0}`,
          160,
          startY + 1,
        );

        startY += 10;

        // =========================
        // Question Text
        // =========================

        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);

        const questionText =
          q.title
            ?.filter((x) => x.descType === "text")
            ?.map((x) => stripHTML(x.description))
            ?.join(" ") || "";

        const splitQuestion = doc.splitTextToSize(questionText, 180);

        doc.text(splitQuestion, 10, startY);

        startY += splitQuestion.length * 6;

        // =========================
        // Question Images
        // =========================

        const questionImages =
          q.title
            ?.filter((x) => x.descType === "img")
            ?.map((x) => x.description) || [];

        for (let imgUrl of questionImages) {
          const base64 = imageCache[imgUrl];

          if (base64) {
            checkPageBreak(50);

            doc.addImage(base64, "JPEG", 10, startY, 80, 45);

            startY += 50;
          }
        }

        // =========================
        // Options
        // =========================

        if (q.questionType !== 3 && q.options?.length) {
          startY += 4;

          q.options.forEach((opt, optIndex) => {
            checkPageBreak(20);

            const optionLabel = String.fromCharCode(65 + optIndex);

            const optionText =
              opt.descType === "text" ? stripHTML(opt.description) : "";

            const splitOption = doc.splitTextToSize(
              `${optionLabel}. ${optionText}`,
              165,
            );

            // Correct Option Highlight
            if (opt.isCorrect) {
              doc.setTextColor(0, 128, 0);
              doc.setFont("helvetica", "bold");
            } else {
              doc.setTextColor(0, 0, 0);
              doc.setFont("helvetica", "normal");
            }

            doc.text(splitOption, 15, startY);

            startY += splitOption.length * 5;

            // Option Image
            if (opt.descType === "img") {
              const base64 = imageCache[opt.description];

              if (base64) {
                checkPageBreak(35);

                doc.addImage(base64, "JPEG", 20, startY, 50, 30);

                startY += 35;
              }
            }

            // Correct Tick
            if (opt.isCorrect) {
              doc.setFont("helvetica", "bold");

              doc.text("✓", 185, startY - 3);

              doc.setTextColor(0, 0, 0);
            }
          });
        }

        // =========================
        // NAT Range
        // =========================

        if (q.questionType === 3 && q.range) {
          checkPageBreak(20);

          doc.setFont("helvetica", "italic");

          doc.text(`Answer Range: ${q.range.r1} to ${q.range.r2}`, 15, startY);

          startY += 8;
        }

        // =========================
        // Explanation
        // =========================

        if (q.ansDescription?.text) {
          checkPageBreak(30);

          doc.setFillColor(248, 250, 252);

          doc.rect(10, startY - 3, 185, 8, "F");

          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);

          doc.text("Explanation", 12, startY + 2);

          startY += 10;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);

          const explanation = stripHTML(q.ansDescription.text);

          const splitExplanation = doc.splitTextToSize(explanation, 180);

          doc.text(splitExplanation, 12, startY);

          startY += splitExplanation.length * 5;
        }

        // =========================
        // Divider
        // =========================

        startY += 8;

        doc.setDrawColor(220, 220, 220);

        doc.line(10, startY, pageWidth - 10, startY);

        startY += 10;
      }

      // =========================
      // New Page Between Parts
      // =========================

      if (startY > 260 && partIndex !== questionData.length - 1) {
        doc.addPage();
        startY = 10;
      }
    }

    doc.save(`${questionData[0].fileName}.pdf`);
  };

  // ── Active file item (for right panel) ────────────────────────────────────
  const activeItem =
    fileQueue.find((i) => i.id === activeFileId) ?? fileQueue[0] ?? null;
  const activeQuestions = activeItem?.questions ?? [];

  function formatFileName(name) {
    return name
      .replace(/\.pdf$/i, "")
      .replace(/[_-]+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function sanitizeForJSON(obj) {
    if (obj === null || obj === undefined) return obj;
    if (obj instanceof Element || obj instanceof Node) return null; // strip DOM nodes
    if (typeof obj !== "object") return obj;
    if (Array.isArray(obj)) return obj.map(sanitizeForJSON);

    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => !(v instanceof Element) && !(v instanceof Node))
        .map(([k, v]) => [k, sanitizeForJSON(v)]),
    );
  }
  /**************Re order question base on section******************* */
  function reorderQuestions(questions, sections) {
    // Build section order map: sectionId -> section index (for sorting)
    const sectionOrderMap = {};
    sections.forEach((section, sectionIndex) => {
      sectionOrderMap[section._id] = sectionIndex;
    });

    // Sort questions by section order, then by existing order within section
    const sorted = [...questions].sort((a, b) => {
      const sectionA = sectionOrderMap[a.section.sectionId] ?? 999;
      const sectionB = sectionOrderMap[b.section.sectionId] ?? 999;

      if (sectionA !== sectionB) return sectionA - sectionB;

      // Within same section, preserve original relative order
      return a.order - b.order;
    });

    // Reassign order sequentially starting from 1
    return sorted.map((q, index) => ({
      ...q,
      order: index + 1,
    }));
  }
  // ── Save examset ───────────────────────────────────────────────────────────
  const saveExamset = (completedItem) => {
    return new Promise((resolve, reject) => {
      const isPracticeTest = false;
      const duration =
        completedItem?.questionTypes?.duration || completedItem?.totalQ * 1.5;

      const totalMarksVal = completedItem?.questionTypes?.totalMarks;
      const reorderedQuestions = reorderQuestions(
        completedItem.questions,
        completedItem?.questionTypes?.sectionData,
      );

      const examSetObj = {
        order: 1,
        examType: 4,
        isFreeUser: false,
        isPracticeTest,
        isPYQ: true,
        isSectionWise: completedItem?.questionTypes?.isModelSet,
        getAdmitCard: false,
        isActive: true,
        examDate: moment().format(),
        createdByAI: true,
        duration,
        totalQuestions: completedItem?.totalQ,
        totalAddedQuestion: completedItem?.totalQ,
        examPatternId: completedItem?.questionTypes?._id,
        name: formatFileName(completedItem?.file?.name),
        totalMarks: totalMarksVal,
        QuestionData: reorderedQuestions, // ✅ use completed item's questions
        sections: completedItem?.questionTypes?.sectionData,
        examYear: completedItem?.year,
        examMonth: completedItem?.month,
        isPart: false,
      };

      const cleaned = sanitizeForJSON(examSetObj);

      let parsed;
      parsed = [
        {
          ...cleaned,
          baseCourseId: examStream?._id,
          examTypeId: exam?._id,
          ...(subExam ? { subExamTypeId: subExam?._id } : {}),
        },
      ];

      dispatch(
        addExamSetWithQuestions({ examSetData: parsed }, (res) => {
          if (res?.success) {
            resolve(res);
          } else {
            reject(new Error(res?.message || "Save failed"));
          }
        }),
      );
    });
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    page: {
      fontFamily: "Inter,sans-serif",
      maxWidth: 1000,
      margin: "0 auto",
      padding: 10,
      background: "#0f172a",
      minHeight: "400px",
      color: "#e2e8f0",
    },
    drop: {
      border: `2px dashed ${dragOver ? "#6366f1" : "#334155"}`,
      borderRadius: 12,
      padding: 24,
      textAlign: "center",
      cursor: "pointer",
      background: dragOver ? "#1e1b4b" : "#1e293b",
      transition: "all 0.2s",
    },
    card: {
      background: "#1e293b",
      borderRadius: 10,
      padding: 16,
      marginTop: 14,
      border: "1px solid #334155",
    },
    btn: (c, dis) => ({
      padding: "7px 14px",
      borderRadius: 8,
      border: "none",
      cursor: dis ? "not-allowed" : "pointer",
      fontWeight: 600,
      fontSize: 12,
      background: dis ? "#1e293b" : c,
      color: dis ? "#475569" : "#fff",
      transition: "all 0.2s",
    }),
    btnSm: (c, dis) => ({
      padding: "4px 10px",
      borderRadius: 6,
      border: "none",
      cursor: dis ? "not-allowed" : "pointer",
      fontWeight: 600,
      fontSize: 11,
      background: dis ? "#1e293b" : c,
      color: dis ? "#475569" : "#fff",
    }),
    inp: {
      background: "#0f172a",
      border: "1px solid #334155",
      borderRadius: 8,
      padding: "5px 8px",
      color: "#e2e8f0",
      fontSize: 13,
    },
    inpSm: {
      background: "#0f172a",
      border: "1px solid #334155",
      borderRadius: 6,
      padding: "4px 6px",
      color: "#e2e8f0",
      fontSize: 12,
      width: 64,
      textAlign: "center",
    },
    badge: (bg, col) => ({
      fontSize: 11,
      padding: "2px 7px",
      borderRadius: 99,
      fontWeight: 600,
      background: bg,
      color: col,
      whiteSpace: "nowrap",
    }),
    tab: (a) => ({
      padding: "6px 12px",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 500,
      borderRadius: "6px 6px 0 0",
      border: "none",
      background: a ? "#6366f1" : "transparent",
      color: a ? "#fff" : "#94a3b8",
    }),
    opt: (ok) => ({
      display: "flex",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 6,
      marginTop: 4,
      background: ok ? "#064e3b" : "#0f172a",
      border: `1px solid ${ok ? "#10b981" : "#1e293b"}`,
      alignItems: "flex-start",
    }),
    logBox: {
      background: "#0f172a",
      borderRadius: 8,
      padding: 10,
      maxHeight: 150,
      overflowY: "auto",
      fontFamily: "monospace",
      fontSize: 11,
      marginTop: 6,
    },
    logColors: {
      info: "#94a3b8",
      success: "#34d399",
      warn: "#fbbf24",
      error: "#f87171",
    },
    queueItem: (isActive, status) => ({
      background: isActive ? "#1e1b4b" : "#1e293b",
      borderRadius: 10,
      padding: "10px 14px",
      marginTop: 10,
      border: `1px solid ${isActive ? "#6366f1" : status === "done" ? "#10b981" : status === "failed" ? "#ef4444" : "#334155"}`,
      transition: "all 0.2s",
    }),
  };

  const statusBadge = (status, count) => {
    const map = {
      pending: [s.badge("#1e3a5f", "#60a5fa"), "⏳ Pending"],
      uploading: [s.badge("#312e81", "#a5b4fc"), "📤 Uploading"],
      counting: [s.badge("#1e3a5f", "#818cf8"), "🔍 Counting"],
      extracting: [s.badge("#78350f", "#fbbf24"), "⚡ Extracting"],
      done: [s.badge("#064e3b", "#34d399"), `✅ Done (${count}q)`],
      failed: [s.badge("#4c0519", "#f87171"), "❌ Failed"],
    };
    const [style, label] = map[status] ?? [
      s.badge("#1e293b", "#94a3b8"),
      status,
    ];
    return <span style={style}>{label}</span>;
  };

  const pendingCount = fileQueue.filter(
    (i) => i.status === "pending" || i.status === "failed",
  ).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="contentBox">
        <PageTitle name={"PYQ Question Extraction"} />
        <div className="formDiv contentScrollbar">
          <div className="ai-command-center-container">
            <div className="command-content row mx-0">
              {/* ── LEFT SECTION ── */}
              <div className="Ai-left-section col-md-6">
                {/* Quick Command Templates */}
                <div className="quick-templates-card">
                  <div className="card-header-Ai">
                    <Zap size={18} className="header-icon" />
                    <h2>Quick Command Templates</h2>
                  </div>
                  <div className="p-3">
                    <div className="form-outer-AI">
                      <div className="form-field-AI">
                        <label>EXAM *</label>
                        <Dropdown
                          labelName="Exam Name"
                          setInputText={onChangeExam}
                          options={examList}
                          value={exam}
                        />
                      </div>
                      {subExamList?.length > 0 && (
                        <div className="form-field-AI">
                          <label>SUB EXAM *</label>
                          <Dropdown
                            labelName="Sub Exam"
                            setInputText={onChangeSubExam}
                            options={subExamList}
                            value={subExam}
                          />
                        </div>
                      )}
                      <div className="form-field-AI">
                        <label>EXAM STREAM *</label>
                        <Dropdown
                          labelName="Exam Stream"
                          setInputText={onChangeExamStream}
                          options={examStreamList}
                          value={examStream}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Command — Multi-file upload */}
                <div className="ai-command-card position-relative">
                  <div className="card-header-Ai">
                    <div className="header-left">
                      <Wand2 size={18} className="header-icon" />
                      <h2>AI Command — Multi-File Queue</h2>
                    </div>
                    <button className="knowledge-base-btn">
                      Uses Knowledge Base
                    </button>
                  </div>

                  <div className="command-input-section p-1">
                    <div style={s.page}>
                      {/* Drop Zone */}
                      <div
                        style={s.drop}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          handleFileDrop(e.dataTransfer.files);
                        }}
                        onClick={() => fileRef.current.click()}
                      >
                        <div style={{ fontSize: 28 }}>📂</div>
                        <p
                          style={{
                            color: "#94a3b8",
                            fontSize: 13,
                            margin: "6px 0 2px",
                          }}
                        >
                          Drag & drop multiple PDFs / images or click to add
                        </p>
                        <p
                          style={{ color: "#475569", fontSize: 11, margin: 0 }}
                        >
                          Supports multiple files — extracts one by one in order
                        </p>
                        <input
                          ref={fileRef}
                          type="file"
                          accept="image/*,.pdf"
                          multiple
                          style={{ display: "none" }}
                          onChange={(e) => handleFileDrop(e.target.files)}
                        />
                      </div>

                      {/* Queue list */}
                      {fileQueue.map((item) => (
                        <div
                          key={item.id}
                          style={s.queueItem(
                            activeFileId === item.id,
                            item.status,
                          )}
                        >
                          {/* Row 1: filename + status + remove */}
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              flexWrap: "wrap",
                              marginBottom: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                color: "#e2e8f0",
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.fileType === "pdf" ? "📕" : "🖼️"}{" "}
                              {item.file.name}
                            </span>
                            {statusBadge(item.status, item.questions.length)}
                            {item.status !== "extracting" &&
                              item.status !== "uploading" && (
                                <button
                                  style={s.btnSm("#4c0519", false)}
                                  onClick={() => removeFromQueue(item.id)}
                                >
                                  ✕
                                </button>
                              )}
                          </div>

                          {/* Row 2: year / month / total / auto-detect / extract */}
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Pattern
                              </span>
                              <select
                                style={{ ...s.inpSm, width: 60 }}
                                value={item.questionTypes?._id}
                                onChange={(e) => {
                                  const selected = patternList.find(
                                    (p) => p._id === e.target.value,
                                  );
                                  updateQueueItem(item.id, {
                                    questionTypes: selected,
                                  });
                                }}
                                disabled={
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              >
                                {patternList.map((item, i) => {
                                  return (
                                    <option key={i} value={item?._id}>
                                      {item?.name}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Year
                              </span>
                              <input
                                style={s.inpSm}
                                type="number"
                                min={2000}
                                max={2099}
                                value={item.year}
                                onChange={(e) =>
                                  updateQueueItem(item.id, {
                                    year: e.target.value,
                                  })
                                }
                                disabled={
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              />
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Month
                              </span>
                              <select
                                style={{ ...s.inpSm, width: 60 }}
                                value={item.month}
                                onChange={(e) =>
                                  updateQueueItem(item.id, {
                                    month: e.target.value,
                                  })
                                }
                                disabled={
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              >
                                {MONTH_NAMES.map((name, i) => {
                                  const val = String(i + 1).padStart(2, "0");
                                  return (
                                    <option key={val} value={val}>
                                      {name}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Total Q
                              </span>
                              <input
                                style={s.inpSm}
                                type="number"
                                min={1}
                                max={500}
                                placeholder="e.g. 90"
                                value={item.manualTotal}
                                onChange={(e) =>
                                  updateQueueItem(item.id, {
                                    manualTotal: e.target.value,
                                  })
                                }
                                disabled={
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              />
                              {item.totalQ && item.totalQ !== "?" && (
                                <span style={s.badge("#1e3a5f", "#60a5fa")}>
                                  Detected: {item.totalQ}
                                </span>
                              )}
                            </div>

                            <div
                              style={{
                                marginLeft: "auto",
                                display: "flex",
                                gap: 6,
                              }}
                            >
                              {/* Auto-detect button */}
                              <button
                                style={s.btnSm(
                                  "#334155",
                                  isRunningQueue ||
                                    item.status === "extracting",
                                )}
                                disabled={
                                  isRunningQueue || item.status === "extracting"
                                }
                                onClick={() => countQuestionsForItem(item)}
                                title="Auto-detect question count"
                              >
                                🔍 Count
                              </button>

                              {/* Individual extract button */}
                              {item.status !== "done" && (
                                <button
                                  style={s.btnSm(
                                    "#6366f1",
                                    isRunningQueue || !item.manualTotal,
                                  )}
                                  disabled={isRunningQueue || !item.manualTotal}
                                  onClick={() => extractSingleItem(item)}
                                >
                                  ▶ Extract
                                </button>
                              )}

                              {/* View button */}
                              <button
                                style={s.btnSm(
                                  activeFileId === item.id
                                    ? "#4f46e5"
                                    : "#334155",
                                  false,
                                )}
                                onClick={() => {
                                  setActiveFileId(item.id);
                                  setActiveTab(
                                    item.questions.length > 0
                                      ? "questions"
                                      : "preview",
                                  );
                                }}
                              >
                                👁 View
                              </button>
                            </div>
                          </div>

                          {/* Extracting spinner */}
                          {item.status === "extracting" && (
                            <div
                              style={{
                                marginTop: 8,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 14,
                                  height: 14,
                                  border: "2px solid #334155",
                                  borderTop: "2px solid #6366f1",
                                  borderRadius: "50%",
                                  animation: "spin 1s linear infinite",
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                Extracting in batches of {BATCH_SIZE}...
                              </span>
                              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            </div>
                          )}

                          {/* Per-item logs */}
                          {item.logs.length > 0 && (
                            <details style={{ marginTop: 6 }}>
                              <summary
                                style={{
                                  fontSize: 11,
                                  color: "#6366f1",
                                  cursor: "pointer",
                                }}
                              >
                                📋 Logs ({item.logs.length})
                              </summary>
                              <div style={s.logBox}>
                                {item.logs.map((l, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      color: s.logColors[l.type],
                                      marginBottom: 2,
                                    }}
                                  >
                                    <span style={{ color: "#475569" }}>
                                      [{l.time}]
                                    </span>{" "}
                                    {l.msg}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      ))}

                      {/* Queue controls */}
                      {fileQueue.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          {isRunningQueue ? (
                            <button
                              style={s.btn("#ef4444", false)}
                              onClick={cancelQueue}
                            >
                              🛑 Stop Queue
                            </button>
                          ) : (
                            <button
                              style={s.btn("#6366f1", pendingCount === 0)}
                              onClick={runQueue}
                              disabled={pendingCount === 0}
                            >
                              🚀 Extract All Pending ({pendingCount})
                            </button>
                          )}
                          <button
                            style={s.btn("#475569", isRunningQueue)}
                            onClick={() => {
                              setFileQueue([]);
                              setActiveFileId(null);
                              fileIdCacheRef.current = {};
                              imageMapCacheRef.current = {};
                            }}
                            disabled={isRunningQueue}
                          >
                            🗑 Clear All
                          </button>
                          <span
                            style={{
                              fontSize: 12,
                              color: "#64748b",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {
                              fileQueue.filter((i) => i.status === "done")
                                .length
                            }
                            /{fileQueue.length} done
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── RIGHT SECTION ── */}
              <div className="Ai-right-section col-md-6">
                <div className="output-card position-relative">
                  <div className="output-header">
                    <div className="output-title">
                      <span className="output-dot">●</span>
                      <h2>AI Planning Output</h2>
                    </div>
                  </div>

                  <div className="ai-container">
                    <div
                      className="output-content question-output"
                      style={{
                        fontFamily: "monospace",
                        fontSize: 13,
                        color: "#e2e8f0",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {/* File selector tabs (for files with content) */}
                      {fileQueue.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            gap: 4,
                            flexWrap: "wrap",
                            padding: "8px 8px 0",
                            borderBottom: "1px solid #1e293b",
                            overflowX: "auto",
                          }}
                        >
                          {fileQueue.map((item) => (
                            <button
                              key={item.id}
                              style={{
                                padding: "5px 10px",
                                cursor: "pointer",
                                fontSize: 11,
                                fontWeight: 500,
                                borderRadius: "6px 6px 0 0",
                                border: `1px solid ${activeFileId === item.id ? "#6366f1" : "#334155"}`,
                                borderBottom: "none",
                                background:
                                  activeFileId === item.id
                                    ? "#6366f1"
                                    : "#1e293b",
                                color:
                                  activeFileId === item.id ? "#fff" : "#94a3b8",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => {
                                setActiveFileId(item.id);
                                setActiveTab(
                                  item.questions.length > 0
                                    ? "questions"
                                    : "preview",
                                );
                              }}
                            >
                              {item.year}/{item.month}
                              {item.status === "extracting" && " ⚡"}
                              {item.status === "done" &&
                                ` ✅ ${item.questions.length}q`}
                              {item.status === "failed" && " ❌"}
                            </button>
                          ))}
                        </div>
                      )}

                      {activeItem && (
                        <>
                          {/* Content tabs */}
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              borderBottom: "1px solid #1e293b",
                              padding: "0 4px",
                            }}
                          >
                            {activeItem.pyqAnalysis && (
                              <button
                                style={s.tab(activeTab === "pyqAnalysis")}
                                onClick={() => setActiveTab("pyqAnalysis")}
                              >
                                📷 Analysis
                              </button>
                            )}
                            {activeItem.preview && (
                              <button
                                style={s.tab(activeTab === "preview")}
                                onClick={() => setActiveTab("preview")}
                              >
                                📷 Preview
                              </button>
                            )}
                            {activeQuestions.length > 0 && (
                              <button
                                style={s.tab(activeTab === "questions")}
                                onClick={() => setActiveTab("questions")}
                              >
                                ❓ Questions ({activeQuestions.length})
                              </button>
                            )}
                            {activeQuestions.length > 0 && (
                              <button
                                style={s.tab(activeTab === "json")}
                                onClick={() => setActiveTab("json")}
                              >
                                {"{ }"} JSON
                              </button>
                            )}
                            {/* File info */}
                            <div
                              style={{
                                marginLeft: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "0 8px",
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                {activeItem.file.name} · {activeItem.year}/
                                {activeItem.month}
                              </span>
                            </div>
                          </div>

                          {/* Analysis tab */}
                          {activeTab === "pyqAnalysis" &&
                            activeItem.pyqAnalysis && (
                              <div
                                style={{
                                  marginTop: 12,
                                  padding: "0 8px",
                                  maxWidth: "100%",
                                  borderRadius: 8,
                                  border: "1px solid #334155",
                                  background: "#1e293b",
                                  // color: "#334155",
                                }}
                              >
                                <ReactMarkdown>
                                  {activeItem.pyqAnalysis}
                                </ReactMarkdown>
                              </div>
                            )}

                          {/* Preview tab */}
                          {activeTab === "preview" && activeItem.preview && (
                            <div style={{ marginTop: 12, padding: "0 8px" }}>
                              {activeItem.fileType === "image" ? (
                                <img
                                  src={activeItem.preview}
                                  alt="preview"
                                  style={{
                                    maxWidth: "100%",
                                    borderRadius: 8,
                                    border: "1px solid #334155",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    ...s.card,
                                    textAlign: "center",
                                    color: "#94a3b8",
                                  }}
                                >
                                  <div
                                    style={{ fontSize: 40, marginBottom: 8 }}
                                  >
                                    📕
                                  </div>
                                  <strong
                                    style={{
                                      color: "#a5b4fc",
                                      display: "block",
                                      marginBottom: 4,
                                    }}
                                  >
                                    {activeItem.file.name}
                                  </strong>
                                  <div
                                    style={{ fontSize: 12, color: "#64748b" }}
                                  >
                                    {(activeItem.file.size / 1024).toFixed(1)}{" "}
                                    KB ·{activeItem.year}/{activeItem.month}
                                  </div>
                                  {fileIdCacheRef.current[activeItem.id] && (
                                    <span
                                      style={{
                                        ...s.badge("#064e3b", "#34d399"),
                                        marginTop: 8,
                                        display: "inline-block",
                                      }}
                                    >
                                      ✅ Uploaded to Claude
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Questions tab */}
                          {activeTab === "questions" && (
                            <div
                              style={{ marginTop: 12, padding: "0 8px" }}
                              ref={(el) => {
                                if (el) typesetMath();
                              }}
                            >
                              {/* Stats */}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginBottom: 12,
                                }}
                              >
                                {[
                                  ["Total", activeQuestions.length, "#a5b4fc"],
                                  [
                                    "Easy",
                                    activeQuestions.filter(
                                      (q) => q.complexityLevel === 1,
                                    ).length,
                                    "#34d399",
                                  ],
                                  [
                                    "Medium",
                                    activeQuestions.filter(
                                      (q) => q.complexityLevel === 2,
                                    ).length,
                                    "#fbbf24",
                                  ],
                                  [
                                    "Hard",
                                    activeQuestions.filter(
                                      (q) => q.complexityLevel === 3,
                                    ).length,
                                    "#f87171",
                                  ],
                                  [
                                    "MCQ",
                                    activeQuestions.filter(
                                      (q) => q.questionType === 1,
                                    ).length,
                                    "#818cf8",
                                  ],
                                  [
                                    "MSQ",
                                    activeQuestions.filter(
                                      (q) => q.questionType === 2,
                                    ).length,
                                    "#c084fc",
                                  ],
                                  [
                                    "NAT",
                                    activeQuestions.filter(
                                      (q) => q.questionType === 3,
                                    ).length,
                                    "#38bdf8",
                                  ],
                                ].map(([label, val, col]) => (
                                  <div
                                    key={label}
                                    style={{
                                      background: "#1e293b",
                                      borderRadius: 8,
                                      padding: "8px 14px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: col,
                                        display: "block",
                                      }}
                                    >
                                      {val}
                                    </span>
                                    <span
                                      style={{ fontSize: 11, color: "#64748b" }}
                                    >
                                      {label}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* Question cards */}
                              {activeQuestions.map((q, i) => {
                                const complexityStyle = {
                                  1: ["#14532d", "#86efac"],
                                  2: ["#78350f", "#fcd34d"],
                                  3: ["#7f1d1d", "#fca5a5"],
                                };
                                const complexityLabel = {
                                  1: "Easy",
                                  2: "Medium",
                                  3: "Hard",
                                };
                                const qtypeLabel = {
                                  1: "MCQ",
                                  2: "MSQ",
                                  3: "NAT",
                                };
                                const [bg, col] = complexityStyle[
                                  q.complexityLevel
                                ] ?? ["#1e293b", "#94a3b8"];
                                const titleHtml = (q.title || [])
                                  .map((block) => {
                                    if (block.descType === "img") {
                                      return `<img src="${block.description}" alt="question image" style="max-width:100%;border-radius:4px;margin:6px 0;display:block;" />`;
                                    }
                                    return block.description ?? "";
                                  })
                                  .join("");

                                return (
                                  <div key={i} style={s.card}>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 6,
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        marginBottom: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#6366f1",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Q{q.order}
                                      </span>
                                      {q.section?.sectionId && (
                                        <span
                                          style={s.badge("#1e3a5f", "#60a5fa")}
                                        >
                                          Sec {q.section.sectionId.slice(-4)}
                                        </span>
                                      )}
                                      <span style={s.badge(bg, col)}>
                                        {complexityLabel[q.complexityLevel] ??
                                          "—"}
                                      </span>
                                      {q.questionType && (
                                        <span
                                          style={s.badge("#1e3a5f", "#818cf8")}
                                        >
                                          {qtypeLabel[q.questionType] ??
                                            `Type ${q.questionType}`}
                                        </span>
                                      )}
                                      {q.unit && (
                                        <span
                                          style={s.badge("#1a2e1a", "#4ade80")}
                                        >
                                          {q.unit}
                                        </span>
                                      )}
                                      <span
                                        style={{
                                          marginLeft: "auto",
                                          fontSize: 11,
                                          color: "#475569",
                                        }}
                                      >
                                        +{q.marks ?? 0} / -
                                        {q.negativeMarks ?? 0}
                                      </span>
                                    </div>

                                    <div
                                      style={{
                                        fontSize: 13,
                                        color: "#e2e8f0",
                                        lineHeight: 1.6,
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: sanitize(titleHtml),
                                      }}
                                    />

                                    {q.questionType !== 3 && (
                                      <div style={{ marginTop: 8 }}>
                                        {q.options?.map((opt, j) => (
                                          <div
                                            key={j}
                                            style={s.opt(opt.isCorrect)}
                                          >
                                            <span
                                              style={{
                                                fontWeight: 700,
                                                color: opt.isCorrect
                                                  ? "#10b981"
                                                  : "#64748b",
                                                minWidth: 18,
                                                fontSize: 13,
                                              }}
                                            >
                                              {String.fromCharCode(65 + j)}.
                                            </span>
                                            <span
                                              style={{
                                                fontSize: 13,
                                                color: opt.isCorrect
                                                  ? "#a7f3d0"
                                                  : "#cbd5e1",
                                              }}
                                              dangerouslySetInnerHTML={{
                                                __html:
                                                  opt.descType === "img"
                                                    ? `<img src="${opt.description}" alt="option image" style="max-width:100%;border-radius:4px;margin:6px 0;display:block;" />`
                                                    : sanitize(opt.description),
                                              }}
                                            />
                                            {opt.isCorrect && (
                                              <span
                                                style={{
                                                  marginLeft: "auto",
                                                  color: "#10b981",
                                                }}
                                              >
                                                ✓
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {q.questionType === 3 && q.range && (
                                      <div
                                        style={{
                                          marginTop: 8,
                                          padding: "6px 10px",
                                          background: "#0f172a",
                                          borderRadius: 6,
                                          fontSize: 12,
                                          color: "#94a3b8",
                                        }}
                                      >
                                        Answer range:{" "}
                                        <strong style={{ color: "#f0abfc" }}>
                                          {q.range.r1}
                                        </strong>{" "}
                                        to{" "}
                                        <strong style={{ color: "#f0abfc" }}>
                                          {q.range.r2}
                                        </strong>
                                        {q.decimalPlaces > 0 && (
                                          <span
                                            style={{
                                              marginLeft: 8,
                                              color: "#64748b",
                                            }}
                                          >
                                            (up to {q.decimalPlaces} decimal
                                            places)
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {q.ansDescription?.text && (
                                      <details style={{ marginTop: 8 }}>
                                        <summary
                                          style={{
                                            cursor: "pointer",
                                            fontSize: 12,
                                            color: "#6366f1",
                                            fontWeight: 600,
                                          }}
                                        >
                                          📖 Explanation
                                        </summary>
                                        <div
                                          style={{
                                            marginTop: 6,
                                            padding: 10,
                                            background: "#0f172a",
                                            borderRadius: 6,
                                            fontSize: 12,
                                            color: "#94a3b8",
                                            lineHeight: 1.7,
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: sanitize(
                                              q.ansDescription.text,
                                            ),
                                          }}
                                        />
                                      </details>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* JSON tab */}
                          {activeTab === "json" && (
                            <div style={{ marginTop: 12, padding: "0 8px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 8,
                                }}
                              >
                                <span
                                  style={{ fontSize: 13, color: "#94a3b8" }}
                                >
                                  {activeQuestions.length} questions ·{" "}
                                  {activeItem.year}/{activeItem.month}
                                </span>
                                <button
                                  style={s.btn("#b91010", false)}
                                  onClick={() =>
                                    handleExport([
                                      {
                                        fileName: activeItem.file.name,
                                        questions: activeQuestions,
                                      },
                                    ])
                                  }
                                >
                                  ⬇️ Download PDF
                                </button>
                                <button
                                  style={s.btn("#10b981", false)}
                                  onClick={() => copyJSON(activeQuestions)}
                                >
                                  📋 Copy JSON
                                </button>
                              </div>
                              <div
                                style={{
                                  background: "#0f172a",
                                  border: "1px solid #334155",
                                  borderRadius: 8,
                                  padding: 14,
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  overflow: "auto",
                                  maxHeight: 480,
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "monospace",
                                }}
                              >
                                {JSON.stringify(
                                  {
                                    totalExtracted: activeQuestions.length,
                                    year: activeItem.year,
                                    month: activeItem.month,
                                    questions: activeQuestions,
                                  },
                                  null,
                                  2,
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Empty state */}
                      {fileQueue.length === 0 && (
                        <div
                          style={{
                            padding: 40,
                            textAlign: "center",
                            color: "#475569",
                          }}
                        >
                          <div style={{ fontSize: 40, marginBottom: 12 }}>
                            📂
                          </div>
                          <p style={{ fontSize: 14, margin: 0 }}>
                            Upload PYQ papers on the left to get started
                          </p>
                          <p
                            style={{
                              fontSize: 12,
                              marginTop: 6,
                              color: "#334155",
                            }}
                          >
                            Add year & month for each file, then extract all at
                            once
                          </p>
                        </div>
                      )}
                    </div>

                    {/* <div className="d-flex justify-content-center gap-2 align-items-center my-2 mx-3">
                      <button
                        type="button"
                        className="execute-btn"
                        onClick={saveExamset}
                        disabled={
                          !activeItem || activeItem.questions.length === 0
                        }
                      >
                        <Zap size={18} />
                        <span>
                          Save Data{" "}
                          {activeItem &&
                            `(${activeItem.year}/${activeItem.month})`}
                        </span>
                      </button>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* {isOpen && (
          <SubmitExamsetPopup
            isOpen={isOpen}
            exam={exam}
            examStream={examStream}
            setIsOpen={setIsOpen}
            saveExamset={saveExamset}
            activeTemplate={activeTemplate}
          />
        )} */}
      </div>
    </>
  );
}
